import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { useToast } from '../hooks/use-toast';
import { 
  Shield, 
  Users, 
  Search, 
  CheckCircle, 
  XCircle,
  Key,
  Eye,
  Edit
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { Checkbox } from './ui/checkbox';

const API = process.env.REACT_APP_BACKEND_URL || '';

function PermissionsManagement({ user }) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [rolePermissions, setRolePermissions] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userPermissions, setUserPermissions] = useState(null);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchRolePermissions();
    fetchAvailablePermissions();
    fetchUsers();
  }, []);

  const fetchRolePermissions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/permissions/roles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRolePermissions(response.data.roles);
    } catch (error) {
      console.error('Error fetching role permissions:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les permissions des rôles',
        variant: 'destructive'
      });
    }
  };

  const fetchAvailablePermissions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/permissions/available`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailablePermissions(response.data.permissions);
    } catch (error) {
      console.error('Error fetching available permissions:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchUserPermissions = async (userId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/permissions/user/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserPermissions(response.data);
      const userInfo = users.find(u => u.id === userId);
      setSelectedUser(userInfo);
      setDialogOpen(true);
    } catch (error) {
      console.error('Error fetching user permissions:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les permissions de l\'utilisateur',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const updateUserPermissions = async (userId, grantedPerms, revokedPerms) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/permissions/user/${userId}`, {
        granted_permissions: grantedPerms,
        revoked_permissions: revokedPerms
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: 'Succès',
        description: 'Permissions mises à jour avec succès'
      });
      
      setDialogOpen(false);
      setUserPermissions(null);
      setSelectedUser(null);
    } catch (error) {
      console.error('Error updating permissions:', error);
      toast({
        title: 'Erreur',
        description: 'Erreur lors de la mise à jour des permissions',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const togglePermission = (permission, type) => {
    if (!userPermissions) return;

    const newUserPerms = { ...userPermissions };
    
    if (type === 'grant') {
      const grantedSet = new Set(newUserPerms.granted_permissions);
      if (grantedSet.has(permission)) {
        grantedSet.delete(permission);
      } else {
        grantedSet.add(permission);
        // Retirer des révoquées si présent
        const revokedSet = new Set(newUserPerms.revoked_permissions);
        revokedSet.delete(permission);
        newUserPerms.revoked_permissions = Array.from(revokedSet);
      }
      newUserPerms.granted_permissions = Array.from(grantedSet);
    } else {
      const revokedSet = new Set(newUserPerms.revoked_permissions);
      if (revokedSet.has(permission)) {
        revokedSet.delete(permission);
      } else {
        revokedSet.add(permission);
        // Retirer des accordées si présent
        const grantedSet = new Set(newUserPerms.granted_permissions);
        grantedSet.delete(permission);
        newUserPerms.granted_permissions = Array.from(grantedSet);
      }
      newUserPerms.revoked_permissions = Array.from(revokedSet);
    }
    
    setUserPermissions(newUserPerms);
  };

  const getPermissionStatus = (permission) => {
    if (!userPermissions) return 'default';
    if (userPermissions.granted_permissions.includes(permission)) return 'granted';
    if (userPermissions.revoked_permissions.includes(permission)) return 'revoked';
    if (userPermissions.default_permissions.includes(permission)) return 'default';
    return 'none';
  };

  const filteredUsers = users.filter(u => 
    u.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Grouper les permissions par ressource
  const groupedPermissions = {};
  availablePermissions.forEach(perm => {
    if (!groupedPermissions[perm.resource]) {
      groupedPermissions[perm.resource] = [];
    }
    groupedPermissions[perm.resource].push(perm);
  });

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Shield className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-3xl font-bold">Gestion des Permissions</h1>
            <p className="text-gray-600 mt-1">
              Système de permissions atomiques pour un contrôle granulaire des accès
            </p>
          </div>
        </div>
      </div>

      <Tabs defaultValue="roles" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="roles">Permissions par Rôle</TabsTrigger>
          <TabsTrigger value="users">Permissions par Utilisateur</TabsTrigger>
        </TabsList>

        {/* Onglet Permissions par Rôle */}
        <TabsContent value="roles" className="space-y-4">
          <Card className="card">
            <CardHeader>
              <CardTitle>Permissions par Défaut des Rôles</CardTitle>
              <CardDescription>
                Permissions attribuées automatiquement à chaque rôle. Les permissions "manage" incluent toutes les actions CRUD.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {rolePermissions.map((roleData) => (
                  <div key={roleData.role} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <Key className="w-5 h-5 text-blue-600" />
                        <h3 className="text-lg font-semibold">{roleData.display_name}</h3>
                        <Badge variant="outline">{roleData.role}</Badge>
                      </div>
                      <Badge className="bg-blue-100 text-blue-800">
                        {roleData.permissions.length} permission(s)
                      </Badge>
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      {roleData.permissions.slice(0, 20).map((perm) => (
                        <Badge 
                          key={perm}
                          variant="secondary"
                          className="text-xs"
                        >
                          {perm}
                        </Badge>
                      ))}
                      {roleData.permissions.length > 20 && (
                        <Badge variant="outline" className="text-xs">
                          +{roleData.permissions.length - 20} autres...
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Onglet Permissions par Utilisateur */}
        <TabsContent value="users" className="space-y-4">
          <Card className="card">
            <CardHeader>
              <CardTitle>Modifier les Permissions d'un Utilisateur</CardTitle>
              <CardDescription>
                Accordez ou révoquez des permissions spécifiques pour un utilisateur
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Barre de recherche */}
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Rechercher un utilisateur..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Liste des utilisateurs */}
              <div className="space-y-2">
                {filteredUsers.map((u) => (
                  <div
                    key={u.id}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex items-center space-x-3">
                      <Users className="w-5 h-5 text-gray-500" />
                      <div>
                        <p className="font-medium">
                          {u.first_name} {u.last_name}
                        </p>
                        <p className="text-sm text-gray-500">{u.email}</p>
                      </div>
                      <Badge variant="outline">{u.role}</Badge>
                    </div>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => fetchUserPermissions(u.id)}
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Modifier
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog de modification des permissions utilisateur */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Permissions de {selectedUser?.first_name} {selectedUser?.last_name}
            </DialogTitle>
            <DialogDescription>
              Rôle: <Badge variant="outline">{userPermissions?.role}</Badge>
            </DialogDescription>
          </DialogHeader>

          {userPermissions && (
            <div className="space-y-4">
              {/* Permissions accordées */}
              {userPermissions.granted_permissions.length > 0 && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <h4 className="font-semibold text-green-900 mb-2 flex items-center">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Permissions Accordées ({userPermissions.granted_permissions.length})
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {userPermissions.granted_permissions.map(perm => (
                      <Badge key={perm} className="bg-green-600">
                        {perm}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Permissions révoquées */}
              {userPermissions.revoked_permissions.length > 0 && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <h4 className="font-semibold text-red-900 mb-2 flex items-center">
                    <XCircle className="w-4 h-4 mr-2" />
                    Permissions Révoquées ({userPermissions.revoked_permissions.length})
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {userPermissions.revoked_permissions.map(perm => (
                      <Badge key={perm} className="bg-red-600">
                        {perm}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Toutes les permissions disponibles groupées */}
              <div className="space-y-4">
                <h4 className="font-semibold">Toutes les Permissions</h4>
                {Object.entries(groupedPermissions).map(([resource, perms]) => (
                  <div key={resource} className="border rounded-lg p-3">
                    <h5 className="font-medium mb-2 text-blue-600 capitalize">
                      {resource}
                    </h5>
                    <div className="grid grid-cols-2 gap-2">
                      {perms.map(perm => {
                        const status = getPermissionStatus(perm.code);
                        return (
                          <div key={perm.code} className="flex items-center space-x-2">
                            <Checkbox
                              checked={status === 'granted' || status === 'default'}
                              disabled={status === 'default'}
                              onCheckedChange={(checked) => {
                                if (status === 'none') {
                                  togglePermission(perm.code, 'grant');
                                } else if (status === 'granted') {
                                  togglePermission(perm.code, 'grant');
                                } else if (status === 'revoked') {
                                  togglePermission(perm.code, 'revoke');
                                }
                              }}
                            />
                            <label className={`text-sm ${
                              status === 'default' ? 'text-gray-500' :
                              status === 'granted' ? 'text-green-600 font-medium' :
                              status === 'revoked' ? 'text-red-600 line-through' :
                              'text-gray-700'
                            }`}>
                              {perm.code}
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {/* Boutons d'action */}
              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  Annuler
                </Button>
                <Button
                  onClick={() => updateUserPermissions(
                    selectedUser.id,
                    userPermissions.granted_permissions,
                    userPermissions.revoked_permissions
                  )}
                  disabled={loading}
                >
                  {loading ? 'Enregistrement...' : 'Enregistrer'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default PermissionsManagement;
